import { useEffect, useState } from "react";
import {
  ArrowDown, BarChart3, Boxes, LayoutDashboard, Package, PackageCheck, PackageX, Search,
  ShoppingBag, ShoppingCart, Store, TrendingUp, Truck, Users, Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { FinalCta, SectionHead, StepsLine, usePageMeta } from "../components/service/ServiceBits";
import api from "../lib/api";
import VideoHeroBg from "../components/service/VideoHeroBg";

const FLOW_IMG = "https://static.prod-images.emergentagent.com/jobs/6e217b82-4821-4792-98ef-42642f657e6a/images/5cc4481e85e8ad1faa4d21d7d0e4787c515afee54e57ab025102f1012fa57c57.jpeg";

const AUDIENCE = [
  { icon: Store, label: "D2C Brands" }, { icon: ShoppingBag, label: "Shopify Sellers" },
  { icon: ShoppingCart, label: "WooCommerce Sellers" }, { icon: Users, label: "Instagram Sellers" },
  { icon: LayoutDashboard, label: "Marketplace Sellers" }, { icon: Package, label: "Amazon Sellers" },
  { icon: Boxes, label: "Small Businesses" }, { icon: TrendingUp, label: "Growing Brands" },
];

const FEATURES = [
  { icon: Truck, title: "Multi-Courier Network", desc: "Multiple courier partners for every lane and service level." },
  { icon: Zap, title: "Smart Courier Allocation", desc: "Technology picks the right courier for each shipment." },
  { icon: Search, title: "Shipment Tracking", desc: "Live tracking for you and your customers." },
  { icon: PackageX, title: "NDR Management", desc: "Tools to act on non-delivery reports and reduce RTO." },
  { icon: LayoutDashboard, title: "Business Dashboard", desc: "All your shipments in one clear view." },
  { icon: ShoppingBag, title: "Order Management", desc: "Book, manage and follow orders effortlessly." },
  { icon: BarChart3, title: "Shipping Analytics", desc: "Understand costs, performance and delivery trends." },
  { icon: TrendingUp, title: "Scalable Operations", desc: "From 10 orders a month to 10,000+ — the system scales." },
];

const JOURNEY = [
  { n: "01", title: "Customer Orders" }, { n: "02", title: "Order Processing" }, { n: "03", title: "Courier Selection" },
  { n: "04", title: "Label / Shipment" }, { n: "05", title: "Pickup" }, { n: "06", title: "Transit" }, { n: "07", title: "Delivery" },
];

const DASH_STATS = [
  { l: "Orders", v: "1,248" }, { l: "Shipments", v: "1,190" }, { l: "Delivered", v: "1,041" },
  { l: "In Transit", v: "118" }, { l: "NDR", v: "21" }, { l: "RTO", v: "10" },
];
const DASH_BARS = [42, 58, 50, 66, 61, 78, 72, 88, 80, 95, 90, 100];

export default function EcommerceShipping() {
  const [contact, setContact] = useState(null);
  usePageMeta(
    "eCommerce Shipping Solutions for Online Sellers | IndiaFast",
    "Technology-enabled eCommerce shipping for D2C brands and online sellers — multi-courier network, smart allocation, NDR management, dashboards and analytics.",
    "/ecommerce-shipping"
  );
  useEffect(() => { api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ecommerce-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-24 pt-32 sm:pt-36" data-testid="ecommerce-hero">
        <VideoHeroBg mp4="/videos/warehouse.mp4" webm="/videos/warehouse.webm"
                     poster="https://static.prod-images.emergentagent.com/jobs/6e217b82-4821-4792-98ef-42642f657e6a/images/ebdd6072c017a77d6780280f6ed79b8e7d64e52052e8c98ad7e41625ae69ced6.jpeg"
                     posterAlt="IndiaFast fulfilment warehouse" badge="Fulfilment Network" chip="Seller order EC10021 · Packed & sorted" testid="ecommerce-hero-video" />
        <div className="pointer-events-none absolute -right-32 top-10 h-96 w-96 rounded-full bg-saffron/15 blur-3xl" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2">
          <div>
            <p className="animate-fade-up text-xs font-bold uppercase tracking-[0.25em] text-saffron">eCommerce Shipping</p>
            <h1 className="animate-fade-up stagger-1 mt-3 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Shipping Built for <span className="text-saffron">Growing eCommerce</span> Businesses
            </h1>
            <p className="animate-fade-up stagger-2 mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Manage your online shipments with technology-enabled courier solutions designed for sellers and growing brands.
            </p>
            <div className="animate-fade-up stagger-3 mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4">
              <Link to="/register" data-testid="ecommerce-start-cta"
                    className="flex items-center justify-center gap-2 rounded-full bg-saffron px-8 py-3.5 text-sm font-bold text-white shadow-xl shadow-saffron/30 transition-all hover:-translate-y-0.5 hover:bg-saffron-hover">
                Start Shipping
              </Link>
              <Link to="/support" data-testid="ecommerce-talk-cta"
                    className="flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/10 px-8 py-3.5 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/20">
                Talk to Our Team
              </Link>
            </div>
          </div>
          <div className="relative">
            <img src={FLOW_IMG} alt="eCommerce order to delivery ecosystem" className="w-full rounded-3xl border border-white/10 object-cover shadow-2xl" />
          </div>
        </div>
      </section>

      {/* WHO IS IT FOR */}
      <section className="bg-white py-16 sm:py-20" data-testid="ecommerce-audience">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead kicker="Who Is It For?" title="Made for Every Kind of Online Seller" center />
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {AUDIENCE.map((a, i) => (
              <div key={i} data-testid={`ecommerce-audience-${i}`}
                   className="group rounded-2xl border border-slate-200 bg-slate-50 p-5 text-center transition-all hover:-translate-y-1 hover:border-saffron/40 hover:bg-white hover:shadow-lg">
                <span className="mx-auto grid h-11 w-11 place-items-center rounded-xl bg-navy text-saffron transition-colors group-hover:bg-saffron group-hover:text-white"><a.icon className="h-5 w-5" /></span>
                <p className="mt-3 text-sm font-bold text-navy">{a.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="ecommerce-features">
        <SectionHead kicker="eCommerce Shipping Features" title="Everything Your Online Business Needs to Ship Better" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <div key={i} data-testid={`ecommerce-feature-${i}`}
                 className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-saffron/10 text-saffron transition-colors group-hover:bg-saffron group-hover:text-white"><f.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-heading text-base font-bold text-navy">{f.title}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ORDER JOURNEY */}
      <section className="bg-white py-16 sm:py-20" data-testid="ecommerce-journey">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead kicker="Order Journey" title="From Order Placed to Order Delivered" center />
          <StepsLine steps={JOURNEY} testid="ecommerce-steps" cols="lg:grid-cols-7" />
        </div>
      </section>

      {/* DASHBOARD MOCKUP */}
      <section className="relative overflow-hidden bg-navy py-16 sm:py-20 lg:py-24" data-testid="ecommerce-dashboard">
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <SectionHead kicker="Your Command Center" title="A Dashboard That Runs Your Shipping" dark center
                       sub="Orders, shipments, NDR, RTO and shipping cost — all in one place." />
          <div className="mt-12 rounded-3xl border border-white/10 bg-white/5 p-5 shadow-2xl backdrop-blur sm:p-8" data-testid="ecommerce-dashboard-mock">
            <div className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              <span className="ml-3 text-[11px] font-semibold text-slate-400">IndiaFast Seller Dashboard</span>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {DASH_STATS.map((s, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-navy/60 p-3.5" data-testid={`ecommerce-dash-${i}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{s.l}</p>
                  <p className={`mt-1 font-heading text-xl font-extrabold ${s.l === "NDR" || s.l === "RTO" ? "text-amber-400" : "text-white"}`}>{s.v}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 grid gap-4 lg:grid-cols-3">
              <div className="rounded-xl border border-white/10 bg-navy/60 p-4 lg:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Monthly Shipments</p>
                <div className="mt-3 flex h-28 items-end gap-1.5">
                  {DASH_BARS.map((h, i) => (
                    <span key={i} style={{ height: `${h}%` }} className="flex-1 rounded-t bg-gradient-to-t from-saffron/60 to-saffron" />
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-white/10 bg-navy/60 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Shipping Cost</p>
                <p className="mt-2 font-heading text-2xl font-extrabold text-white">₹1.86L</p>
                <p className="text-[11px] text-emerald-400">Avg ₹149 / shipment</p>
                <div className="mt-4 space-y-2">
                  {[["Delivered", 88], ["In Transit", 9], ["NDR / RTO", 3]].map(([l, w]) => (
                    <div key={l}>
                      <p className="flex justify-between text-[10px] font-semibold text-slate-400"><span>{l}</span><span>{w}%</span></p>
                      <div className="mt-1 h-1.5 rounded-full bg-white/10"><span className="block h-full rounded-full bg-emerald-500" style={{ width: `${w}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GROWTH */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="ecommerce-growth">
        <SectionHead kicker="Grow With IndiaFast" title="Your Business Grows. Your Shipping Should Grow With It." center />
        <div className="mx-auto mt-12 flex max-w-3xl items-end justify-center gap-4 sm:gap-8">
          {[["10", "h-16", "Orders"], ["100", "h-24", "Orders"], ["1,000", "h-36", "Orders"], ["10,000+", "h-48", "Orders"]].map(([v, h, l], i) => (
            <div key={i} className="flex flex-col items-center gap-2" data-testid={`ecommerce-growth-${i}`}>
              <p className="font-heading text-sm font-extrabold text-navy sm:text-lg">{v}</p>
              <span className={`w-14 rounded-t-2xl bg-gradient-to-t from-navy to-saffron sm:w-20 ${h}`} />
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{l}</p>
              {i < 3 && <ArrowDown className="hidden" />}
            </div>
          ))}
        </div>
      </section>

      <FinalCta testid="ecommerce-final-cta"
                img="https://images.pexels.com/photos/7857540/pexels-photo-7857540.jpeg?auto=compress&cs=tinysrgb&w=1920"
                title="Ready to Simplify Your Shipping?" primaryLabel="Start Shipping" primaryTo="/register" primaryTestid="ecommerce-final-start" />
      <Footer contact={contact} />
    </div>
  );
}
